// =============================================================================
// COOKIE CONSENT BANNER — GDPR/ePrivacy Compliance
// =============================================================================
// A consent banner that appears for EU/UK visitors (or when configured).
// Key compliance requirements:
//
// 1. NO non-essential cookies/scripts load BEFORE consent is given
// 2. "Accept" and "Reject" buttons have EQUAL visual weight
// 3. Optional "Customize" / "Settings" trigger opens granular modal
// =============================================================================

import { createPortal } from 'react-dom';
import { useConsent } from '../hooks/useConsent.js';
import './CookieConsent.css';

export interface CookieConsentProps {
  /** Heading text (translated via i18n) */
  heading?: string;
  /** Description text explaining what cookies are used for */
  description?: string;
  /** Accept button text */
  acceptText?: string;
  /** Reject button text */
  rejectText?: string;
  /** Link to the full privacy policy */
  privacyPolicyUrl?: string;
  /** Privacy policy link text */
  privacyPolicyText?: string;
  /** Callback to open granular cookie settings modal */
  onOpenSettings?: () => void;
}

export function CookieConsent({
  heading = 'We value your privacy',
  description = 'We use cookies to enhance your browsing experience and analyze site traffic. You can choose which cookies to accept.',
  acceptText = 'Accept all',
  rejectText = 'Reject all',
  privacyPolicyUrl = '/privacy',
  privacyPolicyText = 'Privacy Policy',
  onOpenSettings,
}: CookieConsentProps) {
  const { status, acceptAll, rejectAll } = useConsent();

  // Only show the banner when the user hasn't decided yet or expired
  if ((status !== 'undecided' && status !== 'expired') || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="cookie-consent" role="dialog" aria-label="Cookie consent" aria-modal="false">
      <div className="cookie-consent-inner container">
        <div className="cookie-consent-text">
          <h2 className="cookie-consent-heading">{heading}</h2>
          <p className="cookie-consent-description">
            {description}{' '}
            {onOpenSettings ? (
              <button
                type="button"
                className="cookie-consent-link"
                onClick={onOpenSettings}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                  color: 'inherit',
                }}
              >
                {privacyPolicyText}
              </button>
            ) : (
              <a href={privacyPolicyUrl} className="cookie-consent-link">
                {privacyPolicyText}
              </a>
            )}
          </p>
        </div>

        <div className="cookie-consent-actions">
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className="btn btn-ghost btn-sm cookie-consent-btn"
              type="button"
            >
              Customize
            </button>
          )}
          <button
            onClick={rejectAll}
            className="btn btn-secondary cookie-consent-btn"
            type="button"
          >
            {rejectText}
          </button>
          <button
            onClick={acceptAll}
            className="btn btn-secondary cookie-consent-btn"
            type="button"
          >
            {acceptText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
